import { useEffect, useRef } from 'react';
import { EditorState } from 'prosemirror-state';
import { schema as baseSchema } from 'prosemirror-schema-basic';
import { addListNodes } from 'prosemirror-schema-list';
import { exampleSetup } from 'prosemirror-example-setup';
import 'prosemirror-example-setup/style/style.css';
import 'prosemirror-menu/style/menu.css';
import './styles.css';
import { EditorView } from 'prosemirror-view';
import { DOMParser } from 'prosemirror-model';

const ProseMirrorVanilla = ({ content, onChange }) => {
  const editorContainerRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Función para convertir el documento ProseMirror a HTML
  const getHTMLFromState = (state) => {
    const div = document.createElement('div');
    
    // Función recursiva para procesar nodos
    const processNode = (node) => {
      // Crear elemento para este nodo
      let element;
      
      if (node.type.name === 'text') {
        // Nodo de texto - aplicar marks
        let content = node.text;
        
        // Aplicar marks en orden inverso (por cómo funciona ProseMirror)
        if (node.marks) {
          node.marks.slice().reverse().forEach(mark => {
            switch (mark.type.name) {
              case 'strong':
                content = `<strong>${content}</strong>`;
                break;
              case 'em':
                content = `<em>${content}</em>`;
                break;
              case 'link':
                const href = mark.attrs.href;
                const title = mark.attrs.title || '';
                content = `<a href="${href}" title="${title}" target="_blank" rel="noopener noreferrer">${content}</a>`;
                break;
              default:
                break;
            }
          });
        }
        
        return content;
      } else {
        // Nodo estructural
        switch (node.type.name) {
          case 'paragraph':
            element = document.createElement('p');
            break;
          case 'bullet_list':
            element = document.createElement('ul');
            break;
          case 'ordered_list':
            element = document.createElement('ol');
            break;
          case 'list_item':
            element = document.createElement('li');
            break;
          case 'horizontal_rule':
            element = document.createElement('hr');
            break;
          default:
            element = document.createElement('div');
            break;
        }
        
        // Procesar contenido hijos
        node.content.forEach(child => {
          const childContent = processNode(child);
          if (typeof childContent === 'string') {
            element.innerHTML += childContent;
          } else {
            element.appendChild(childContent);
          }
        });
        
        return element;
      }
    };
    
    // Procesar todos los nodos del documento
    state.doc.content.forEach(node => {
      const nodeContent = processNode(node);
      if (typeof nodeContent === 'string') {
        div.innerHTML += nodeContent;
      } else {
        div.appendChild(nodeContent);
      }
    });
    
    return div.innerHTML;
  };

  useEffect(() => {
    if (!editorContainerRef.current) return;

    const mySchema = new baseSchema.constructor({
      nodes: addListNodes(baseSchema.spec.nodes, "paragraph block*", "block"),
      marks: baseSchema.spec.marks
    });

    // Limpiar el contenedor
    editorContainerRef.current.innerHTML = '';
    
    // Crear elemento para el editor
    const editorElement = document.createElement('div');
    editorElement.className = 'prosemirror-editor';
    editorContainerRef.current.appendChild(editorElement);

    // Crear elemento para el contenido inicial
    const contentElement = document.createElement('div');
    contentElement.innerHTML = content || '<p></p>';

    // Inicializar ProseMirror
    const view = new EditorView(editorElement, {
      state: EditorState.create({
        doc: DOMParser.fromSchema(mySchema).parse(contentElement),
        plugins: exampleSetup({ schema: mySchema })
      }),
      dispatchTransaction: (transaction) => {
        if (!viewRef.current) return;
        
        const newState = view.state.apply(transaction);
        view.updateState(newState);
        
        const html = getHTMLFromState(newState);
        if (onChangeRef.current) {
          onChangeRef.current(html);
        }
      }
    });

    viewRef.current = view;

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [content]);

  return <div ref={editorContainerRef} className="prosemirror-wrapper" />;
};

export default ProseMirrorVanilla;
